'use client'

import { filter, find, noop } from 'lodash'
import { FormattedMessage } from 'react-intl'
import { Button, Icon, Loader, Text } from '@vinted/web-ui'

import { InfoCircle16 } from '@vinted/monochrome-icons'

import { useState } from 'react'

import { Screen } from 'constants/tracking/screens'

import { Grid as ItemGrid } from '@marketplace-web/common-components/grid-ui'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { ProductItem } from '@marketplace-web/domain/item-box'
import { ProductItemModel } from '@marketplace-web/domain/product-item-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { GoogleTagManagerEvent, googleTagManagerTrack } from '@marketplace-web/domain/google-track'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import {
  cannotBundleButtonClickEvent,
  cannotBundleButtonViewEvent,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'

import { ErrorBoundary } from '@marketplace-web/shared/error-display'

import { getIsBundlingAllowed } from '../../pages/BundleItems/utils'
import BundlingPolicyDialog from '../BundlingPolicyDialog/BundlingPolicyDialog'

type Props = {
  items: Array<ProductItemModel>
  selectedItems: Array<ProductItemModel>
  isLoading: boolean
  endReached?: boolean
  emptyState?: JSX.Element
  loadItems?: () => void
  updateSelectedItems: (items: Array<ProductItemModel>) => void
  viewingSelf?: boolean
  showFavourite?: boolean
  showPriceBreakdown?: boolean
  selectionLimit?: number
  onItemVisible?: ComponentProps<typeof ItemGrid<ProductItemModel>>['onItemVisible']
  onItemClick?: (item: ProductItemModel, position: number, isSelected: boolean) => void
  bundleTrackingId: string | null
  sellerId: number
}

const ItemsMultiselect = ({
  items,
  selectedItems,
  isLoading,
  endReached,
  emptyState,
  loadItems,
  updateSelectedItems,
  viewingSelf = false,
  showFavourite = false,
  showPriceBreakdown,
  selectionLimit,
  onItemVisible,
  onItemClick,
  bundleTrackingId,
  sellerId,
}: Props) => {
  const translate = useTranslate('items_multiselect')
  const { track } = useTracking()

  const [bundlingPolicyDialogUi, setBundlingPolicyDialogUi] = useState<{
    isOpen: boolean
    selectedItemId?: number
  }>({ isOpen: false })

  const isLimitReached = () => {
    return !!selectedItems.length && selectedItems.length === selectionLimit
  }

  const selectItem = (item: ProductItemModel, index: number) => () => {
    const itemSelected = !!find(selectedItems, { id: item.id })

    if (!itemSelected && isLimitReached()) return

    if (itemSelected) {
      googleTagManagerTrack(GoogleTagManagerEvent.RemoveFromBundle, {
        item_id: item.id,
      })
    }

    const updatedItems = itemSelected
      ? filter(selectedItems, prevItem => prevItem.id !== item.id)
      : [...selectedItems, item]
    const isFirstItemSelected = selectedItems.length === 0 && updatedItems.length === 1

    if (isFirstItemSelected) {
      const cannotBundleEvent = cannotBundleButtonViewEvent({
        listingId: item.id,
        sellerId,
        reasonForNotBundling: ReasonForNotBundling.ShippingSize,
        screen: Screen.Bundling,
        bundleSessionId: bundleTrackingId,
      })
      const selectedItemId = updatedItems[0]?.id

      items.forEach(bundleItem => {
        if (
          !getIsBundlingAllowed({
            item: bundleItem,
            bundleItems: items,
            selectedItemId,
          })
        )
          track(cannotBundleEvent)
      })
    }

    updateSelectedItems(updatedItems)

    if (onItemClick) onItemClick(item, index, !itemSelected)
  }

  const renderItemFooter = (item: ProductItemModel, index: number) => {
    const itemSelected = !!find(selectedItems, { id: item.id })
    const buttonTextSuffix = itemSelected ? 'remove' : 'add'
    const isButtonDisabled = isLimitReached() && !itemSelected
    const trackingData = {
      listingId: item.id,
      sellerId,
      reasonForNotBundling: ReasonForNotBundling.ShippingSize,
      screen: Screen.Bundling,
      bundleSessionId: bundleTrackingId,
    }

    const handleCantBundleButtonClick = () => {
      setBundlingPolicyDialogUi({ isOpen: true, selectedItemId: item.id })
      track(cannotBundleButtonClickEvent(trackingData))
    }

    if (getIsBundlingAllowed({ item, bundleItems: items, selectedItemId: selectedItems[0]?.id })) {
      return (
        <Button
          text={<FormattedMessage id={`items_multiselect.actions.${buttonTextSuffix}`} />}
          theme={itemSelected ? 'warning' : 'primary'}
          size={Button.Size.Small}
          onClick={selectItem(item, index)}
          disabled={isButtonDisabled}
          testId={`${buttonTextSuffix}-button`}
        />
      )
    }

    return (
      <Button
        text={<Text text={translate('actions.disabled')} as="span" type={Text.Type.Caption} />}
        styling={Button.Styling.Flat}
        size={Button.Size.Small}
        theme="muted"
        icon={<Icon name={InfoCircle16} />}
        iconPosition={Button.IconPosition.Right}
        testId="cant-bundle-button"
        onClick={handleCantBundleButtonClick}
      />
    )
  }

  const renderItem: ComponentProps<typeof ItemGrid<ProductItemModel>>['renderItem'] = (
    { data: item },
    index,
  ) => {
    return (
      <ErrorBoundary FallbackComponent={ErrorBoundary.ComponentError}>
        <ProductItem
          item={item}
          renderFooter={renderItemFooter(item, index)}
          showStatus
          viewingSelf={viewingSelf}
          showFavourite={showFavourite}
          showPriceBreakdown={showPriceBreakdown}
          onClick={
            getIsBundlingAllowed({ item, bundleItems: items, selectedItemId: selectedItems[0]?.id })
              ? selectItem(item, index)
              : noop
          }
          isUrlDisabled
        />
      </ErrorBoundary>
    )
  }

  const renderItemsContainer = () => {
    const gridItems = items.map(item => ({ id: item.id, data: item }))

    return <ItemGrid items={gridItems} renderItem={renderItem} onItemVisible={onItemVisible} />
  }

  return (
    <>
      {!loadItems && renderItemsContainer()}
      {isLoading && (
        <div className="u-flexbox u-justify-content-center">
          <Loader size={Loader.Size.Large} testId="items-multiselect--loader" />
        </div>
      )}
      {loadItems && (
        <InfiniteScroll
          renderItems={renderItemsContainer}
          onPageEnd={loadItems}
          cursor={String(items.length)}
        />
      )}
      {!isLoading && endReached && !items.length && (
        <div className="u-flexbox u-justify-content-center u-flex-grow">{emptyState}</div>
      )}
      <BundlingPolicyDialog
        show={bundlingPolicyDialogUi.isOpen}
        bundleTrackingId={bundleTrackingId}
        sellerId={sellerId}
        selectedItemId={bundlingPolicyDialogUi.selectedItemId}
        onClose={() => setBundlingPolicyDialogUi({ isOpen: false })}
      />
    </>
  )
}

export default ItemsMultiselect
